// src/components/PatientDetails/panels/ChatPanel.tsx
// -----------------------------------------------------------------------------
// ChatPanel - Center panel containing the chat interface with domain selector
// -----------------------------------------------------------------------------
// Features:
// - Domain selector positioned inside chat container (top-right)
// - Submitted documents dropdown
// - Chat history with scroll
// - Chat input bar at bottom
// - Glassmorphic styling
// -----------------------------------------------------------------------------

import { Box } from "@mui/material";
import React, { useMemo } from "react";
import ChatInterface from "../sections/UnifiedCaptureHub/ChatInterface";
import { EvidenceDocument } from "types";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

type DomainType = { value: string; label: string };

interface ChatPanelProps {
  /** Available domain types (kept for potential future use) */
  domainTypes?: DomainType[];
  /** Currently selected domains (kept for potential future use) */
  selectedDomains?: string[];
  /** Handler for domain selection change (kept for potential future use) */
  onDomainSelectionChange?: (domains: string[]) => Promise<{ limited: boolean; max: number }>;
  /** Suggested domains from AI (kept for potential future use) */
  suggestedDomains?: string[];
  /** Submitted documents (visualizable files only) */
  submittedDocuments: EvidenceDocument[];
  /** All documents including text-only */
  allDocuments?: Array<{
    id: string;
    file_name?: string;
    file_url?: string;
    file?: string;
    text?: string;
    created_at?: string;
  }>;
  /** Handler for document delete */
  onDocumentDelete?: (id: string) => void;
  /** Handler when submission completes */
  onSubmit: () => void;
  /** Whether extraction is in progress */
  isExtracting?: boolean;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const ChatPanel: React.FC<ChatPanelProps> = ({
  submittedDocuments,
  allDocuments,
  onDocumentDelete,
  onSubmit,
  isExtracting = false,
}) => {
  // Filter and map documents to ensure file_url is always defined
  const validSubmittedDocuments = useMemo(() => {
    return submittedDocuments.filter(doc => doc.file_url || doc.file).map(doc => ({
      ...doc,
      file_url: doc.file_url || doc.file || "",
    }));
  }, [submittedDocuments]);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Chat Interface (ChatInterface has its own NeumorphicCard) */}
      <ChatInterface
        submittedDocuments={validSubmittedDocuments as any}
        allDocuments={allDocuments}
        onDocumentDelete={onDocumentDelete}
        onSubmit={onSubmit}
        isExtracting={isExtracting}
      />
    </Box>
  );
};

export default ChatPanel;
